import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Badge, Button, Empty, List, Modal, Popover, Spin, Tag } from '@douyinfe/semi-ui'
import { IconBell, IconChevronRight } from '@douyinfe/semi-icons'
import { useNavigate } from 'react-router'
import { useIntl } from 'react-intl'
import OneEditorViewer from '@components/OneEditorViewer'
import { fmtDateTime } from '@src/utils/date'
import {
	getMessageById,
	getUnreadList,
	markAllAsRead,
	markAsRead,
	MessageDetailVo,
	SCOPE_ALL,
	UnreadMessageVo
} from '@src/api/sys/message'
import { buildSSEUrl } from '@src/common'
import { useOneSSE } from '@components/OneSSE'

const HeaderMessage: React.FC = () => {
	const { formatMessage: i18nMsg } = useIntl()
	const navigate = useNavigate()
	const [visible, setVisible] = useState(false)
	const [list, setList] = useState<UnreadMessageVo[]>([])
	const [count, setCount] = useState(0)
	const [loading, setLoading] = useState(false)
	const [detail, setDetail] = useState<MessageDetailVo | null>(null)
	const [detailLoading, setDetailLoading] = useState(false)
	const mountedRef = useRef(true)

	// SSE 订阅未读数；URL 拼接统一走 @src/common/buildSSEUrl
	// 后端 payload 结构：{ event: 'unreadCount', data: { count: N }, ts: ... }
	// 心跳事件由 hook 内部独立处理，不进 onMessage
	useOneSSE<{ event: string; data: { count: number }; ts?: number }>({
		url: buildSSEUrl('unreadCount'),
		eventName: 'unreadCount',
		onMessage: (msg) => {
			if (mountedRef.current) setCount(Number(msg?.data?.count) || 0)
		}
	})

	const refreshList = useCallback(async () => {
		setLoading(true)
		try {
			const res = await getUnreadList(8)
			if (mountedRef.current) setList(res.data ?? [])
		} catch {
			/* ignore */
		} finally {
			if (mountedRef.current) setLoading(false)
		}
	}, [])

	// 打开弹层时刷新列表
	const onVisibleChange = (v: boolean) => {
		setVisible(v)
		if (v) {
			refreshList()
		}
	}

	const openDetail = async (item: UnreadMessageVo) => {
		setDetailLoading(true)
		try {
			const res = await getMessageById(item.id)
			setDetail(res.data)
			await markAsRead(item.id)
			setList((prev) => prev.filter((m) => m.id !== item.id))
			setCount((c) => Math.max(0, c - 1))
		} catch {
			/* ignore */
		} finally {
			setDetailLoading(false)
		}
	}

	const closeDetail = () => {
		setDetail(null)
	}

	const handleMarkAll = async () => {
		await markAllAsRead()
		setList([])
		setCount(0)
	}

	const goAll = () => {
		setVisible(false)
		navigate('/sys/message')
	}

	const content = (
		<div style={{ width: 360 }}>
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					padding: '4px 4px 8px',
					borderBottom: '1px solid var(--semi-color-border)'
				}}
			>
				<span style={{ fontWeight: 600 }}>{i18nMsg({ id: 'app.message.title' })}</span>
				{count > 0 && (
					<Button type="tertiary" theme="borderless" onClick={handleMarkAll}>
						{i18nMsg({ id: 'app.message.markAllRead' })}
					</Button>
				)}
			</div>

			<Spin spinning={loading}>
				{list.length === 0 ? (
					<div style={{ padding: '32px 0' }}>
						<Empty description={i18nMsg({ id: 'app.message.empty' })} />
					</div>
				) : (
					<List
						style={{ maxHeight: 360, overflowY: 'auto' }}
						dataSource={list}
						renderItem={(item) => (
							<List.Item
								style={{ cursor: 'pointer', padding: '8px 4px' }}
								onClick={() => openDetail(item)}
								main={
									<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
										<span
											style={{
												fontWeight: 600,
												color: 'var(--semi-color-text-0)',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap'
											}}
										>
											{item.title}
										</span>
										{item.contextPreview && (
											<span
												style={{
													fontSize: 12,
													color: 'var(--semi-color-text-2)',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
													whiteSpace: 'nowrap'
												}}
											>
												{item.contextPreview}
											</span>
										)}
										<span style={{ fontSize: 12, color: 'var(--semi-color-text-2)' }}>
											{fmtDateTime(item.createTime)}
										</span>
									</div>
								}
							/>
						)}
					/>
				)}
			</Spin>

			<div
				style={{
					paddingTop: 8,
					borderTop: '1px solid var(--semi-color-border)',
					textAlign: 'center'
				}}
			>
				<Button type="tertiary" theme="borderless" icon={<IconChevronRight />} iconPosition="right" onClick={goAll}>
					{i18nMsg({ id: 'app.message.viewAll' })}
				</Button>
			</div>
		</div>
	)

	return (
		<>
			<Popover
				content={content}
				visible={visible}
				onVisibleChange={onVisibleChange}
				trigger="hover"
				placement="bottomRight"
				showArrow
			>
				<Badge count={count} overflowCount={99} dot={count > 0 ? false : false}>
					<Button
						theme="borderless"
						icon={<IconBell size="extra-large" />}
						style={{
							color: 'var(--semi-color-text-2)',
							marginRight: '12px'
						}}
						aria-label={i18nMsg({ id: 'app.message.title' })}
					/>
				</Badge>
			</Popover>

			<Modal
				title={detail?.title}
				visible={!!detail || detailLoading}
				onCancel={closeDetail}
				footer={null}
				width={620}
				afterClose={() => setDetail(null)}
			>
				<Spin spinning={detailLoading}>
					{detail && (
						<>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: 8,
									fontSize: 12,
									color: 'var(--semi-color-text-2)',
									marginBottom: 12,
									flexWrap: 'wrap'
								}}
							>
								<span>{detail.senderName}</span>
								<span>·</span>
								<span>{fmtDateTime(detail.createTime)}</span>
								{detail.scope === SCOPE_ALL && (
									<Tag size="small" color="blue">
										{i18nMsg({ id: 'app.message.scope.all' })}
									</Tag>
								)}
							</div>
							<OneEditorViewer value={detail.context} />
						</>
					)}
				</Spin>
			</Modal>
		</>
	)
}

export default HeaderMessage
